import { useMemo, useRef } from 'react';
import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { useAvContext } from '@context/AvContextProvider';
import { QueryKey } from '@fabric-ui/utils';
import useQueryObject from '@hooks/useQueryObjectSql';
import { isNullOrUndefined } from '@utils/utils';
import { gql } from 'graphql-request';
import { GetRiskScoringConfigResponse } from './types';
import { getFindingTypeDisplayName } from './utils';

const getMinFormula = (fieldName: string) => `MIN(${fieldName})`;

const FIELD_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*$/;
const isValidFieldName = (fieldName: string) => FIELD_NAME_PATTERN.test(fieldName);

export const useGetRiskScoringConfig = ({ projectionName }: { projectionName: string }) =>
  useGqlQuery<GetRiskScoringConfigResponse>({
    queryKey: [QueryKey.GetRiskScoringConfig, projectionName],
    query: GET_RISK_SCORING_CONFIG,
    variables: { projectionName },
  });

export const useGetAllRiskScoringConfig = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useGqlQuery<GetRiskScoringConfigResponse>({
    queryKey: [QueryKey.GetAllRiskScoringConfig],
    query: GET_ALL_RISK_SCORING_CONFIG,
    enabled,
  });

export const useCreateOrUpdateRiskScoringConfig = (onSuccess?: () => Promise<any>) =>
  useGqlMutation({
    mutationKey: [QueryKey.CreateOrUpdateRiskScoringConfig],
    mutation: CREATE_OR_UPDATE_RISK_SCORE,
    onSuccess,
  });

export const GET_RISK_SCORING_PREVIEW = gql`
  query riskScoringPreview($input: RiskScoringCreationRequestInput!, $selectivityFilter: filterScalar) {
    riskScoringPreview(input: $input, selectivityFilter: $selectivityFilter) {
      rows
      scriptError
      internalError
    }
  }
`;

export const useGetRiskScoringPreview = ({
  input,
  selectivityFilter,
  enabled = true,
  staleTime,
}: {
  input: any;
  selectivityFilter?: any;
  enabled?: boolean;
  staleTime?: number;
}) =>
  useGqlQuery({
    queryKey: [QueryKey.GetRiskScoringPreview, input, selectivityFilter],
    query: GET_RISK_SCORING_PREVIEW,
    variables: { input, selectivityFilter },
    select: (data: any) => data?.rows || [],
    enabled,
    staleTime,
  });

const GET_ALL_RISK_SCORING_CONFIG = gql`
  query getAllRiskScoringConfig {
    getAllRiskScoringConfig {
      projectionToRiskScores {
        projectionName
        riskScore {
          id
          accountId
          projectionName
          updatedByUserId
          updatedAt
          config {
            baseScoreWeightContribution {
              projectionName
              weight
              aggType
            }
          }
        }
      }
      projectionToBaseScores {
        projectionName
        baseScore {
          id
          accountId
          projectionName
          config {
            configTypes {
              type
              isDefault
              factorGroups {
                name
                weight
                factors {
                  displayName
                  fieldName
                  fallback
                  scoringRules {
                    value
                    filter
                    adjustmentType
                  }
                }
              }
            }
          }
        }
      }
      riskScoreProjectionToBaseScoresMap {
        riskScoreProjectionName
        baseScores {
          projectionName
          allowedAggTypes
        }
      }
    }
  }
`;

const CREATE_OR_UPDATE_RISK_SCORE = gql`
  mutation createOrUpdateRiskScoringConfig($input: RiskScoringCreationRequestInput!) {
    createOrUpdateRiskScoringConfig(input: $input)
  }
`;

export const useFieldsHasData = ({ projectionName, fieldNames }: { projectionName: string; fieldNames: (string | undefined)[] }) => {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();
  const isKnownProjection = !!aggProjs[projectionName]?.pathAlias;

  const cacheRef = useRef<Map<string, Map<string, boolean>>>(new Map());
  if (!cacheRef.current.has(projectionName)) {
    cacheRef.current.set(projectionName, new Map());
  }
  const projectionCache = cacheRef.current.get(projectionName)!;

  const uniqueFieldNames = useMemo(
    () => Array.from(new Set(fieldNames.filter((fieldName): fieldName is string => !!fieldName && isValidFieldName(fieldName)))),
    [fieldNames]
  );

  const uncachedFieldNames = useMemo(
    () => uniqueFieldNames.filter(fieldName => !projectionCache.has(fieldName)),
    [uniqueFieldNames, projectionCache]
  );

  const metrics = useMemo(
    () => (isKnownProjection ? uncachedFieldNames.map((fieldName, index) => ({ name: getMinFormula(fieldName), alias: `f${index}` })) : []),
    [isKnownProjection, uncachedFieldNames]
  );

  const { data, isLoading } = useQueryObject({
    queryObject: {
      select: { dims: [], metrics },
      projectionId: { name: projectionName, builtIn: false },
      sorting: [],
      top: { size: 1, groupOthers: false },
    },
    options: { enabled: metrics.length > 0 },
  });

  const populatedByField = useMemo(() => {
    const row = data?.[0];
    if (row) {
      uncachedFieldNames.forEach((fieldName, index) => projectionCache.set(fieldName, !isNullOrUndefined(row[`f${index}`])));
    }

    return Object.fromEntries(
      uniqueFieldNames.map(fieldName => [
        fieldName,
        projectionCache.has(fieldName) ? projectionCache.get(fieldName) : isLoading ? undefined : false,
      ])
    ) as Record<string, boolean | undefined>;
  }, [data, isLoading, uniqueFieldNames, uncachedFieldNames, projectionCache]);

  return { populatedByField, isLoading: isLoading && uncachedFieldNames.length > 0 };
};

export const useFindingTypeOptions = (existingTypes: Set<string>) => {
  const { data: findingTypesData, isLoading: isLoadingFindingTypes } = useQueryObject({
    queryObject: {
      select: {
        dims: [
          {
            name: 'finding.type',
          },
        ],
        metrics: [],
      },
      distinct: true,
      projectionId: {
        name: 'uber_findings',
        builtIn: true,
      },
      sorting: [],
    },
  });

  const findingTypeOptions = useMemo(
    () =>
      (findingTypesData || [])
        .map(f => f['finding.type'])
        .filter(type => !isNullOrUndefined(type) && !existingTypes.has(type))
        .map(type => ({
          title: getFindingTypeDisplayName(type),
          value: type,
        })),
    [findingTypesData, existingTypes]
  );

  return { findingTypeOptions, isLoadingFindingTypes };
};

const GET_RISK_SCORING_CONFIG = gql`
  query getRiskScoringConfig($projectionName: String!) {
    getRiskScoringConfig(projectionName: $projectionName) {
      projectionToRiskScores {
        projectionName
        riskScore {
          id
          accountId
          projectionName
          updatedByUserId
          updatedAt
          config {
            baseScoreWeightContribution {
              projectionName
              weight
              aggType
            }
          }
        }
      }
      projectionToBaseScores {
        projectionName
        baseScore {
          id
          accountId
          projectionName
          config {
            configTypes {
              type
              factorGroups {
                name
                weight
                factors {
                  displayName
                  fieldName
                  fallback
                  scoringRules {
                    value
                    filter
                    adjustmentType
                  }
                }
              }
            }
          }
        }
      }
      riskScoreProjectionToBaseScoresMap {
        riskScoreProjectionName
        baseScores {
          projectionName
          allowedAggTypes
        }
      }
    }
  }
`;
